import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchCampaignsRequest,
  fetchDeviceDataRequest,
  fetchLocationDataRequest,
  fetchTimeSeriesDataRequest,
  fetchXMedAggStatsForTimeRange,
} from '../actions';
import { RestDataIdentifier, RestQueryParams } from '../types';
import { sortAndStringifyQueryParams } from '../utils';
import { useReduxData } from './useReduxData';
import { useRestQueryParams } from './useRestQueryParams';

// Mapping RestDataIdentifiers to their corresponding fetch actions
const fetchActions: Record<RestDataIdentifier, (...args: any[]) => any> = {
  [RestDataIdentifier.DEVICE]: fetchDeviceDataRequest,
  [RestDataIdentifier.LOCATION]: fetchLocationDataRequest,
  [RestDataIdentifier.TIME_SERIES]: fetchTimeSeriesDataRequest,
  [RestDataIdentifier.CAMPAIGNS_FOR_METRICS]: fetchCampaignsRequest,
  [RestDataIdentifier.OVERVIEW]: fetchXMedAggStatsForTimeRange,
  [RestDataIdentifier.PDF_REPORT]: () => null,
};

/**
 * Compare query parameters by stringifying and sorting them.
 * @param {RestQueryParams} queryParams1 - The first set of query parameters.
 * @param {RestQueryParams} queryParams2 - The second set of query parameters.
 * @returns {boolean} - True if the query parameters are equal, false otherwise.
 */
const areQueryParamsEqual = (
  queryParams1: RestQueryParams,
  queryParams2: RestQueryParams,
): boolean => {
  return sortAndStringifyQueryParams(queryParams1) === sortAndStringifyQueryParams(queryParams2);
};

/**
 * Custom hook to fetch and manage data from REST endpoints.
 *
 * @param {RestDataIdentifier} restDataIdentifier - The identifier for the type of data to fetch.
 * @returns {Object} An object containing the fetched data, loading state, and error state.
 * @returns {Record<string, any>} data - The fetched data.
 * @returns {boolean} loading - The loading state.
 * @returns {boolean} error - The error state.
 */
export const useRestData = (
  restDataIdentifier: RestDataIdentifier,
): {
  data: Record<string, any>;
  loading: boolean;
  error: boolean;
} => {
  const dispatch = useDispatch();

  const queryData = useRestQueryParams(restDataIdentifier);
  const { previousQueryFilters, ...reduxData } = useReduxData(restDataIdentifier);

  useEffect(() => {
    if (!(queryData?.campaignFilters?.mediums || queryData?.campaignFiltersOpt?.mediums)) return;

    // If query parameters have changed, dispatch the fetch action to get new data
    if (!areQueryParamsEqual(queryData, previousQueryFilters)) {
      dispatch(fetchActions[restDataIdentifier](queryData));
    }
  }, [dispatch, queryData, previousQueryFilters]);

  return reduxData;
};
